import { GOOGLE_API_SCRIPT_URL } from '@/utils/firebase/googleApiDiagnostics/googleApi'

// `gapi` is the global the Google API script defines once it has loaded and run.
type WindowWithGoogleApi = Window & { gapi?: { load?: unknown } }

const isGoogleApiLoaded = (): boolean => !!(window as WindowWithGoogleApi).gapi?.load

// Checks whether the Google API script can actually load and run. This catches blocking a network probe cannot
// see: extension filter rules commonly scope themselves to script requests (`resourceTypes: ["script"]`) and let
// a fetch straight through, so the only reliable test is to load the script the same way Firebase does.
//
// Only meaningful once the network path is known to be open - see `isGoogleApiReachable` - otherwise a blocked
// network fails this check too and gets misreported to the customer as a browser problem.
//
// This is not a throwaway probe: on success `window.gapi.load` is defined, so Firebase reuses it rather than
// requesting the script again (see `loadGapi` in `@firebase/auth`).
export const isGoogleApiScriptLoadable = (): Promise<boolean> => {
  if (isGoogleApiLoaded()) {
    return Promise.resolve(true)
  }

  // Dedupe concurrent callers so only one script element is ever in flight, while still allowing a later retry.
  if (!inFlightLoad) {
    inFlightLoad = loadGoogleApiScript().finally(() => {
      inFlightLoad = null
    })
  }

  return inFlightLoad
}

let inFlightLoad: Promise<boolean> | null = null

const loadGoogleApiScript = (): Promise<boolean> =>
  new Promise((resolve) => {
    const script = document.createElement('script')
    script.src = GOOGLE_API_SCRIPT_URL
    script.async = true
    script.onload = () => resolve(true)
    script.onerror = () => {
      console.error(`Failed to load ${GOOGLE_API_SCRIPT_URL}, federated login is likely blocked by the browser`)
      resolve(false)
    }
    document.head.appendChild(script)
  })
